import { useState, useEffect, useRef } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import i18next from 'i18next';
import { Storage } from 'aws-amplify';

import { Container, Row, Col, Nav, Card, Badge, Alert, Form, Button, BadgeProps, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useErrorToast } from 'src/hooks/useErrorToast';
import CardPlaceholder from 'src/components/CardPlaceholder/CardPlaceholder';
import { DateRange, ReporterData, UserDTO } from 'src/api';
// @ts-ignore
import './ReporterDetailsPage.scss';
import { FaDownload } from 'react-icons/fa';
import _ from 'lodash';
import TableSkeleton from 'src/components/TableSkeleton/TableSkeleton';
import { formatDate, randomString } from 'src/utils';
import { Formik, FormikHelpers } from 'formik';
import Btn from 'src/components/Btn/Btn';
import { GTMsendEvent } from 'src/utils/trackingGA4';
import toaster from 'src/utils/toaster';
import { ReporterStatusEnum } from 'src/api/models/Enums';
import { useQueryClient } from '@tanstack/react-query';
import { MdRefresh } from 'react-icons/md';
import { useConfirmAction } from 'src/hooks/useConfirmAction';
import toast from 'src/utils/toaster';
import * as Utils from 'src/components/Reporter/reporterUtils';
import { PeriodOpportunitySection, ReporterFilePreviewModal, StatsBar } from 'src/components/Reporter';
import { useReporterDocuments } from 'src/components/Reporter/useReporterDocuments';
import ReporterDocumentationFileUploader from 'src/components/Reporter/ReporterDocumentationFileUploader';
import SpinnerButton from 'src/components/SpinnerButton/SpinnerButton';
import { GiCheckMark } from 'react-icons/gi';
import { useAuth } from 'src/hooks/useAuth';
import RequiredFormLabel from 'src/components/RequiredFormLabel/RequiredFormLabel';

const REPORTER_DETAILS_TAB = "reporter_details_tab";
const REPORTER_OPPORTUNITY_TAB = "reporter_opportunity_tab";

const ReporterDetailsTab = ({ user }: { user?: UserDTO }) => {
	const { apiClient, handleApi } = useApiClient();
	const navigate = useNavigate();
	const { showError } = useErrorToast();
	const queryClient = useQueryClient();
	const { userRole } = useAuth();
	const { confirmAction, isConfirmShown } = useConfirmAction();
	const { reporterDocuments, initDocuments, handleReporterDocument } = useReporterDocuments(user?.role, user?.documents);
	const [showReporterDocModal, setReporterDocModal] = useState(false);
	const [uploadingFileType, setUploadingFileType] = useState<string | null>();
	const reporterDocsRef = useRef<any>(null);
	const [filePreview, setFilePreview] = useState<{ isOpen: boolean; document?: any }>({ isOpen: false });

	if (!user) return null;

	const initialValues: any = Object.assign(user || {}, {
		// @ts-ignore
		provvigioni_dealer: user.provvigioni_dealer || (100 - parseFloat(user.provvigioni_segnalatore || '0')),
	});

	const userStatus = user?.status ? i18next.t(user.status?.toLowerCase()) : 'UNKNOWN';
	const isRejected = user?.status?.toUpperCase() === ReporterStatusEnum.REJECTED;
	const isPending = user?.status?.toUpperCase() === ReporterStatusEnum.PENDING;
	const isApproved = user?.status?.toUpperCase() === ReporterStatusEnum.APPROVED;

	const onSubmit = async (values, { setSubmitting }: FormikHelpers<any>, reloadPage = true) => {
		const formFields = ['given_name', 'family_name', 'codice_fiscale', 'partita_iva', 'phone', 'phone_backup', 'email_backup', 'provvigioni_segnalatore', 'iban', 'business_name', 'reference_backoffice'];

		let body: UserDTO = {
			...user,
			..._.pick(values, formFields)
		};

		if (!body.reference_backoffice) {
			body = _.omit(body, ['reference_backoffice'])
		}

		if (Utils.hasMissingPendingDocuments(userRole, reporterDocuments)) {
			toaster.error(i18next.t('segnalatore_all_documents_required'));
			return;
		}

		await uploadReporterFiles(body);

		setSubmitting(true);
		try {
			await handleApi(apiClient.userController.putRegisterUsers(user.username!, body));
			GTMsendEvent({
				event: 'update_user',
				category: 'user',
				action: 'update',
				label: 'update_user',
			});

			// @ts-ignore
			toaster.success(i18next.t('user_updated_success', { username: user.username }));
			reloadPage && setTimeout(() => navigate(0), 1500);
		} catch (error) {
			showError(error, i18next.t('error_updating_user'));
			throw error;
		} finally {
			reloadPage && setSubmitting(false)
		}
	}

	const userFormValidationFn = (values) => {
		const errors: any = {};
		const REQUIRED_MESSAGE = i18next.t('mandatory');

		if (!values?.given_name) {
			errors.given_name = REQUIRED_MESSAGE;
		}

		if (!values?.family_name) {
			errors.family_name = REQUIRED_MESSAGE;
		}

		if (!values.phone) {
			errors.phone = REQUIRED_MESSAGE;
		}

		if (!values.codice_fiscale && !values.partita_iva) {
			errors.partita_iva = REQUIRED_MESSAGE;
			errors.codice_fiscale = REQUIRED_MESSAGE;
		}

		if (!values.iban?.trim()) {
			errors.iban = REQUIRED_MESSAGE;
		}

		if (!values.business_name?.trim()) {
			errors.business_name = REQUIRED_MESSAGE;
		}
		return errors;
	};

	const statusProps = {
		[ReporterStatusEnum.APPROVED]: { bg: 'success' },
		[ReporterStatusEnum.PENDING]: { bg: 'warning', text: 'dark' },
		[ReporterStatusEnum.REJECTED]: { bg: 'danger' },
		[ReporterStatusEnum.UNKNOWN]: { bg: 'danger' },
	}[user?.status || ReporterStatusEnum.UNKNOWN] as BadgeProps;

	const requestApproval = async (setSubmitting: FormikHelpers<any>['setSubmitting'], values) => {
		try {
			if (isConfirmShown) return;

			if (_.isEmpty(values.iban)) {
				toaster.error(i18next.t('iban_required'));
				return;
			}

			if (Utils.hasMissingPendingDocuments(userRole, reporterDocuments)) {
				toaster.error(i18next.t('segnalatore_all_documents_required'));
				return;
			}

			const confirmed = await confirmAction({
				title: i18next.t('confirm'),
				message: i18next.t('request_approval_confirmation_message'),
			});

			if (!confirmed) return;

			setSubmitting(true);

			await onSubmit(values, { setSubmitting } as FormikHelpers<any>, false);

			await handleApi(apiClient.userController.requestApproval(user?.username!));
			queryClient.invalidateQueries({ queryKey: ['reporters_list'] });
			setTimeout(() => navigate(0), 1500);
		} catch (err) {
			showError(err, i18next.t('error_updating_user'))
		} finally {
			setSubmitting(false)
		}
	}

	const uploadReporterFiles = async (data: any) => {
		try {
			const docs = _.cloneDeep(reporterDocuments);
			const filesToUploadList: any = [];

			for (const doc of docs) {
				if (doc.file) {
					const ext = doc.file.name.split('.').pop();
					filesToUploadList.push({
						file: doc.file,
						key: `${data.username}/${randomString(10)}.${ext}`,
						fileType: doc.code,
						fileName: doc.fileName,
					})
				} else if (doc.key) {
					filesToUploadList.push({
						key: doc.key,
						fileType: doc.code,
						fileName: doc.fileName
					});
				}
			}
			// const existingSepa = user?.documents?.find(doc => doc.fileType === 'MODULO_SEPA_FIRMATO');

			const uploadOps: any[] = filesToUploadList.map(obj => {
				return obj.file ? Storage.put(obj.key, obj.file, {
					contentType: obj.file.type,
					bucket: process.env.REACT_APP_AWS_STORAGE_BUCKET_USER_DOCUMENTS,
				}) : Promise.resolve();
			})

			await Promise.all(uploadOps);

			data.documents = filesToUploadList.map(item => _.omit(item, 'file'));

			// if (existingSepa?.key) {
			// 	data.documents.push(existingSepa);
			// }

		} catch (e) {
			toast.error(i18next.t('error_loading_file'));
		}
	};

	const downloadTemplate = async (templateDocument) => {
		try {
			const downloadLink: string = await Storage.get(
				templateDocument.filename,
				{ bucket: templateDocument.bucket }
			);
			window.open(downloadLink, '_blank');
		} catch (error) {
			console.warn(error)
			toaster.error(i18next.t('cannot_download_file'));
		}
	}

	return (
		<Card className="mt-2">
			<Card.Header>
				<Badge {...statusProps}>{i18next.t('reporter_status')}: {userStatus}</Badge>
				{isRejected && (
					<>
						<div className="fw-bold underline mt-2">{i18next.t('reason_for_reporter_rejection')?.toUpperCase()}</div>
						<div>
							{user?.message || '-'}
						</div>
					</>
				)}
			</Card.Header>
			<Card.Body className="pt-0">
				<Formik
					initialValues={initialValues}
					validate={(values) => userFormValidationFn(values)}
					enableReinitialize
					onSubmit={async (values, formikHelpers) => {
						onSubmit(values, formikHelpers);
					}}
				>
					{({
						values,
						errors,
						touched,
						handleChange,
						handleBlur,
						handleSubmit,
						isSubmitting,
						setSubmitting,
						setFieldValue
					}) => {
						return (
							<Form onSubmit={handleSubmit}>
								<Row>
									<Col md={6}>
										<Form.Group className="mb-3">
											<Form.Label>
												{i18next.t('business_name')}
												<span className="red-ast">*</span>
											</Form.Label>
											<Form.Control
												disabled={isPending || isApproved}
												onChange={handleChange}
												onBlur={handleBlur}
												value={values.business_name}
												name="business_name"
												type="text"
												className={`${errors.business_name && touched.business_name
													? 'error-border'
													: ''
													}`}
											/>
											{errors.business_name && touched.business_name && (
												<small className="text-danger">
													{errors.business_name as string}
												</small>
											)}
										</Form.Group>
									</Col>
									<Col md={6}>
										<Form.Group className="mb-3">
											<Form.Label>
												{i18next.t('ref_user_name')}
												<span className="red-ast">*</span>
											</Form.Label>
											<Form.Control
												disabled={isPending || isApproved}
												onChange={handleChange}
												onBlur={handleBlur}
												value={values.given_name}
												name="given_name"
												type="text"
												className={`${errors.given_name && touched.given_name
													? 'error-border'
													: ''
													}`}
											/>
											{errors.given_name && touched.given_name && (
												<small className="text-danger">
													{errors.given_name as string}
												</small>
											)}
										</Form.Group>
									</Col>

									<Col md={6}>
										<Form.Group className="mb-3">
											<Form.Label>
												{i18next.t('ref_user_surname')}
												<span className="red-ast">*</span>
											</Form.Label>
											<Form.Control
												disabled={isPending || isApproved}
												onChange={handleChange}
												onBlur={handleBlur}
												value={values.family_name}
												name="family_name"
												type="text"
												className={`${errors.family_name && touched.family_name
													? 'error-border'
													: ''
													}`}
											/>
											{errors.family_name && touched.family_name && (
												<small className="text-danger">
													{errors.family_name as string}
												</small>
											)}
										</Form.Group>
									</Col>

									<Col md={6}>
										<Form.Group className="mb-3">
											<Form.Label>
												{i18next.t('fiscal_code')}
												{!values.partita_iva && (
													<span className="red-ast">*</span>
												)}
											</Form.Label>
											<Form.Control
												disabled={isPending || isApproved}
												onChange={handleChange}
												onBlur={handleBlur}
												value={values.codice_fiscale}
												name="codice_fiscale"
												type="text"
												className={`${errors.codice_fiscale &&
													touched.codice_fiscale
													? 'error-border'
													: ''
													}`}
											/>
											{errors.codice_fiscale &&
												touched.codice_fiscale && (
													<small className="text-danger">
														{errors.codice_fiscale as string}
													</small>
												)}
										</Form.Group>
									</Col>

									<Col md={6}>
										<Form.Group className="mb-3">
											<Form.Label>
												{i18next.t('business_fiscal_code')}
												{!values.codice_fiscale && (
													<span className="red-ast">*</span>
												)}
											</Form.Label>
											<Form.Control
												disabled={isPending || isApproved}
												onChange={handleChange}
												onBlur={handleBlur}
												value={values.partita_iva || ''}
												name="partita_iva"
												type="text"
												className={`${errors.partita_iva &&
													touched.partita_iva
													? 'error-border'
													: ''
													}`}
											/>
											{errors.partita_iva &&
												touched.partita_iva && (
													<small className="text-danger">
														{errors.partita_iva as string}
													</small>
												)}
										</Form.Group>
									</Col>

									<Col md={6}>
										<Form.Group className="mb-3">
											<Form.Label>
												{i18next.t('phone')}
												<span className="red-ast">*</span>
											</Form.Label>
											<Form.Control
												disabled={isPending || isApproved}
												onChange={handleChange}
												onBlur={handleBlur}
												value={values.phone}
												className={`${errors.phone && touched.phone
													? 'error-border'
													: ''
													}`}
												name="phone"
												type="text"
											/>
											{errors.phone && touched.phone && (
												<small className="text-danger">
													{errors.phone as string}
												</small>
											)}
										</Form.Group>
									</Col>
									<Col md={6}>
										<Form.Group className="mb-3">
											<Form.Label>Dealer</Form.Label>
											<Form.Control
												disabled={true}
												value={values.reference_partner?.[0]}
												name="reference_partner"
												type="text"
											/>
										</Form.Group>
									</Col>
									<Col md={6}>
										<Form.Group className="mb-3">
											<Form.Label>
												{i18next.t('reporter_commissions_perc')}
												<span className="red-ast">*</span>
											</Form.Label>
											<Form.Control
												disabled={isApproved || isPending}
												onChange={(e) => {
													const value = e.target.value;
													const valueAsNumber = (Number(value) || 0);
													if (valueAsNumber > 100 || valueAsNumber < 0) {
														return;
													}
													setFieldValue('provvigioni_dealer', 100 - valueAsNumber);

													handleChange(e);
												}}
												onBlur={handleBlur}
												value={values.provvigioni_segnalatore}
												name="provvigioni_segnalatore"
												type="number"
												max={100}
												min={0}
												step="any"
												className={`${errors.provvigioni_segnalatore &&
													touched.provvigioni_segnalatore
													? 'error-border'
													: ''
													}`}
											/>
										</Form.Group>
									</Col>
									<Col md={6}>
										<Form.Group className="mb-3">
											<Form.Label>
												{i18next.t('dealer_commissions_perc')}
											</Form.Label>
											<Form.Control
												disabled={true}
												value={values.provvigioni_dealer}
												name="provvigioni_dealer"
												type="number"
												step="any"
											/>
										</Form.Group>
									</Col>
								</Row>

								<Row>
									<Col md={6}>
										<Form.Group className="mb-3">
											<Form.Label>{i18next.t('phone_backup')}</Form.Label>
											<Form.Control
												disabled={isPending}
												onChange={handleChange}
												onBlur={handleBlur}
												value={values.phone_backup || ''}
												name="phone_backup"
												type="text"
											/>
										</Form.Group>
									</Col>
									<Col md={6}>
										<Form.Group className="mb-3">
											<Form.Label>{i18next.t('email_backup')}</Form.Label>
											<Form.Control
												disabled={isPending}
												onChange={handleChange}
												onBlur={handleBlur}
												value={values.email_backup}
												name="email_backup"
												type="text"
											/>
										</Form.Group>
									</Col>
								</Row>

								<Row>
									<Col md={6}>
										<Form.Group className="mb-3">
											<RequiredFormLabel
												label={i18next.t('iban')}
											/>
											<Form.Control
												onChange={handleChange}
												onBlur={handleBlur}
												disabled={isPending || isApproved}
												value={values.iban || ''}
												name="iban"
												type="text"
											/>
											{errors.iban && (
												<small className="text-danger">
													{errors.iban as string}
												</small>
											)}
										</Form.Group>
									</Col>
								</Row>

								<div className="my-3">
									<hr />
									<h6>{i18next.t('documents')}<span className="red-ast">*</span></h6>
									{reporterDocuments.map((doc, index) => (<Row key={index} className="align-items-center mb-2">
										<Col md="auto">
											{false ? (
												<SpinnerButton busy={false} label="" />
											) : (
												<>
													<div className="d-flex align-items-center">
														{doc.isUploaded
															? <GiCheckMark className="text-success me-2" />
															: <span style={{width: 24}}></span>
														}
														{isRejected && doc.templateDocument
															? <Button
																variant="link"
																className="m-0 p-0"
																onClick={async () => downloadTemplate(doc.templateDocument)}
															>
																<span className="me-2">{doc.label}</span>
																<FaDownload className="text-black" />
															</Button>
															: <span className="me-2">{doc.label}</span>}
													</div>
													{doc.isUploaded ? (
														<span
															className={`${doc.key && 'text-decoration-underline'} ms-4`}
															style={{ cursor: doc.key && 'pointer' }}
															onClick={async () => {
																const downloadLink: string = await Storage.get(doc.key, {
																	bucket: process.env.REACT_APP_AWS_STORAGE_BUCKET_USER_DOCUMENTS,
																});
																setFilePreview({ isOpen: true, document: { ...doc, url: downloadLink } })
															}}
														>
															({doc.fileName})
														</span>
													) : null}
												</>
											)}
										</Col>

										<Col className="ms-auto" md="auto">
											{true && doc.createdAt
												? <Badge bg="secondary" className="m-2">{formatDate(doc.createdAt, 'it-IT', true)}</Badge>
												: null
											}
											{doc.key && (
												<Button
													variant="link"
													onClick={async () => {
														const downloadLink: string = await Storage.get(doc.key, {
															bucket: process.env.REACT_APP_AWS_STORAGE_BUCKET_USER_DOCUMENTS,
														});
														window.open(downloadLink, '_blank');
													}}
												>
													<FaDownload />
												</Button>)
											}
											{isRejected && <Btn
												onClick={() => {
													setUploadingFileType(doc.code);
													setReporterDocModal(true)
												}}
											>
												<SpinnerButton label={i18next.t('upload')} busy={false} />
											</Btn>}
										</Col>
									</Row>))}
								</div>
								<div className="float-end">
									{isRejected && (
										<Button
											variant="warning"
											className="me-4"
											onClick={() => requestApproval(setSubmitting, values)}
											disabled={isSubmitting}
										>
											{isSubmitting
												? <Spinner animation="border" size="sm" />
												: <MdRefresh style={{ marginRight: '8px' }} />}
											{i18next.t('request_approval')}
										</Button>)
									}
									{isApproved && (
										<Btn
											type="submit"
											icon="arrow-right"
											className="float-end"
											disabled={isSubmitting}
										>
											{i18next.t('update')}
										</Btn>
									)}

								</div>
							</Form>
						);
					}}
				</Formik>
			</Card.Body>
			{showReporterDocModal && (
				<ReporterDocumentationFileUploader
					ref={reporterDocsRef}
					handleReporterDocument={(file) => handleReporterDocument(uploadingFileType, file)}
					show={showReporterDocModal}
					onHide={() => {
						setReporterDocModal(false);
						setUploadingFileType(null);
					}}
					onSuccess={() => {
						setReporterDocModal(false);
						setUploadingFileType(null);
					}}
				/>
			)}
			<ReporterFilePreviewModal
				isOpen={filePreview.isOpen}
				closePreview={() => setFilePreview({ isOpen: false })}
				document={filePreview.document}
			/>
		</Card>
	)
}

const DEFAULT_PAGINATION: { skip: number; limit: number } = {
	skip: 0,
	limit: 5000,
} as const;

const EmptyState = ({ message }) => {
	return (
		<Alert variant="light">
			{message}
		</Alert>
	)
}

export const ReporterOpportunityTab = ({ reporterUsername }: { reporterUsername?: string }) => {
	const [, setApiResponse] = useState([]);
	const [loading, setLoading] = useState(true);
	const [, setError] = useState<any>(undefined);
	const { showError } = useErrorToast();
	const { apiClient, handleApi } = useApiClient();
	const [dateRange, setDateRange] = useState(Utils.getSlidingDateRange(6, 0));

	const [data, setData] = useState<ReporterData>({
		stats: {
			opportunityCreated: 0,
			opportunityWon: 0,
			provvigioniSegnalatore: 0,
			provvigioniDealerVinte: 0,
		},
		periods: []
	})
	const { username } = useParams<{ username: string }>();

	const fetchReports = async (segnalatore: string, dateRange: DateRange) => {
		setError(null);
		try {
			const response = await handleApi(
				apiClient.salesforceOpportunityController.getMyReports({
					...DEFAULT_PAGINATION,
					...dateRange,
					segnalatore,
				})
			);
			setApiResponse(response);
			setData({
				stats: Utils.computeStats(response.data),
				periods: Utils.groupByMonthYearDesc(response.data)
			})
		} catch (err) {
			setError(err);
			showError(err, i18next.t('error_loading_data'));
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		fetchReports((reporterUsername || username)!, dateRange);
	}, []);

	if (loading) {
		return <TableSkeleton />;
	}

	const statsPeriod = {
		start: formatDate(dateRange.createdDateFrom || '', 'it-IT', false),
		end: formatDate(dateRange.createdDateTo || '', 'it-IT', false),
	};

	return (
		<div>
			<StatsBar className="mb-3" stats={data.stats} period={statsPeriod} />
			{data.periods.length > 0
				? <div className="d-flex flex-column gap-4">
					{data.periods.map((p, idx) => (
						<PeriodOpportunitySection {...p} key={idx} />
					))}
				</div>
				: <EmptyState message={i18next.t("empty_state_reporter")} />}
		</div>
	)
}

const ReporterDetailsPage = () => {
	const { username } = useParams<{ username: string }>();
	const { apiClient, handleApi } = useApiClient();
	const [isLoading, setIsLoading] = useState(true);
	const { showError } = useErrorToast();
	const [userDetail, setUserDetail] = useState();
	const [activeKey, setActiveKey] = useState<string>(REPORTER_DETAILS_TAB);

	const removeAlert = async (userDto: UserDTO) => {
		try {
			if (!userDto.username || !userDto.alert) {
				return;
			}
			const apiResponse = await handleApi(apiClient.userController.removeAlert(userDto.username));
			console.debug('Removed alert', apiResponse);
		} catch (error) {
			console.warn('Failed to remove alert', error);
		}
	}

	const fetchUserDetail = async () => {
		setIsLoading(true);
		try {
			const apiResponse = await handleApi(apiClient.userController.getRegisterSingleUser(username!));
			setUserDetail(apiResponse);
			removeAlert(apiResponse);
		} catch (error) {
			showError(error, i18next.t('error_loading_user_data'))
		} finally {
			setIsLoading(false);
		}
	}

	useEffect(() => {
		username && fetchUserDetail();
	}, []);


	const ActiveTabContent = activeKey === REPORTER_OPPORTUNITY_TAB
		? <ReporterOpportunityTab />
		: <ReporterDetailsTab user={userDetail} />;

	return (
		<Container fluid id="reporter-details-page">
			<Row>
				<Col md={6} className="display-6">
					{username}
				</Col>
			</Row>

			<Nav fill variant="tabs"
				activeKey={activeKey}
				className="mt-4"
				onSelect={(key) => setActiveKey(key || REPORTER_DETAILS_TAB)}
			>
				<Nav.Item>
					<Nav.Link eventKey={REPORTER_DETAILS_TAB}>{i18next.t('reporter_details_tab')}</Nav.Link>
				</Nav.Item>
				<Nav.Item>
					<Nav.Link eventKey={REPORTER_OPPORTUNITY_TAB}>{i18next.t('reporter_opportunity_tab')}</Nav.Link>
				</Nav.Item>
			</Nav>

			<div className="mt-4">
				{isLoading ? <CardPlaceholder /> : ActiveTabContent}
			</div>

		</Container>
	);
};

export default ReporterDetailsPage;
